import type {
  LocationTrackingSettings,
  TrackingPoint,
  TrackingVisitStatus,
} from '../types/locationTracking';

/** Visit status names shared by Tracking Details and the attendance tables. */
export const TRACKING_STATUS_LABELS: Record<TrackingVisitStatus, string> = {
  ACTIVE: 'Active Tracking',
  STARTING: 'Starting tracking',
  COMPLETED: 'Completed Visit',
  EXPIRED: 'Ended without check-out',
  NOT_RECORDED: 'No route recorded',
};

/** Label for a status code from the API; unknown codes show no label. */
export function trackingStatusLabel(status: string | null | undefined): string | null {
  const code = typeof status === 'string' ? status.trim().toUpperCase() : '';
  return code in TRACKING_STATUS_LABELS
    ? TRACKING_STATUS_LABELS[code as TrackingVisitStatus]
    : null;
}

const EARTH_RADIUS_METERS = 6371008.8;

export type GeoFix = {
  latitude: number;
  longitude: number;
  accuracyMeters: number | null;
  altitudeMeters: number | null;
  speedMps: number | null;
  headingDegrees: number | null;
  /** Epoch milliseconds of the fix (device clock). */
  timestamp: number;
};

type RawPosition = {
  coords?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number | null;
    altitude?: number | null;
    speed?: number | null;
    heading?: number | null;
  };
  timestamp?: number;
};

function normalizeCode(value: string | null | undefined): string {
  return typeof value === 'string'
    ? value.trim().toUpperCase().replace(/[\s/-]+/g, '_')
    : '';
}

/** The server decides which check-in work-location codes are tracked. */
export function isTrackedAttendanceSource(
  source: string | null | undefined,
  settings: Pick<LocationTrackingSettings, 'enabled' | 'trackedSources'> | null,
): boolean {
  if (!settings?.enabled) return false;
  const code = normalizeCode(source);
  return Boolean(code) && settings.trackedSources.includes(code);
}

/** Checked in and not yet checked out. */
export function isAttendanceSessionOpen(
  attendance:
    | {
        attendanceId?: number | null;
        checkInTime?: string | null;
        checkOutTime?: string | null;
        isCheckedOut?: boolean;
      }
    | null
    | undefined,
): attendance is { attendanceId: number; checkInTime: string } {
  return Boolean(
    attendance?.attendanceId &&
      attendance.checkInTime &&
      !attendance.checkOutTime &&
      attendance.isCheckedOut !== true,
  );
}

export function distanceMeters(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number {
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const dLat = toRadians(b.latitude - a.latitude);
  const dLon = toRadians(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(a.latitude)) *
      Math.cos(toRadians(b.latitude)) *
      Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}

function measurement(value: number | null | undefined): number | null {
  // iOS reports unknown speed/heading/accuracy as -1.
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

export function toGeoFix(position: RawPosition): GeoFix | null {
  const latitude = Number(position?.coords?.latitude);
  const longitude = Number(position?.coords?.longitude);
  const timestamp = Number(position?.timestamp);
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    Math.abs(latitude) > 90 ||
    Math.abs(longitude) > 180 ||
    !Number.isFinite(timestamp)
  ) {
    return null;
  }
  const altitude = position.coords?.altitude;
  return {
    latitude,
    longitude,
    accuracyMeters: measurement(position.coords?.accuracy),
    altitudeMeters:
      typeof altitude === 'number' && Number.isFinite(altitude) ? altitude : null,
    speedMps: measurement(position.coords?.speed),
    headingDegrees: measurement(position.coords?.heading),
    timestamp,
  };
}

/**
 * Keeps one fix per sample interval, or sooner when the employee has moved at
 * least the distance filter. Inaccurate, duplicate and out-of-order fixes are dropped.
 */
export function shouldRecordFix(
  previous: GeoFix | null,
  next: GeoFix,
  settings: Pick<
    LocationTrackingSettings,
    | 'maxAccuracyMeters'
    | 'sampleIntervalSeconds'
    | 'minSampleIntervalSeconds'
    | 'distanceFilterMeters'
  >,
): boolean {
  if (next.accuracyMeters !== null && next.accuracyMeters > settings.maxAccuracyMeters) {
    return false;
  }
  if (!previous) return true;
  const elapsedMs = next.timestamp - previous.timestamp;
  if (elapsedMs <= 0) return false;
  if (elapsedMs >= settings.sampleIntervalSeconds * 1000) return true;
  return (
    elapsedMs >= settings.minSampleIntervalSeconds * 1000 &&
    distanceMeters(previous, next) >= settings.distanceFilterMeters
  );
}

let pointSequence = 0;

export function toTrackingPoint(
  fix: GeoFix,
  attendanceId: number,
  isBackground: boolean,
): TrackingPoint {
  pointSequence = (pointSequence + 1) % 1000000;
  return {
    // Stable per fix: retries resend the same id, so the server stores it once.
    clientPointId: `${attendanceId}-${fix.timestamp.toString(36)}-${pointSequence.toString(36)}`,
    latitude: fix.latitude,
    longitude: fix.longitude,
    accuracyMeters: fix.accuracyMeters,
    altitudeMeters: fix.altitudeMeters,
    speedMps: fix.speedMps,
    headingDegrees: fix.headingDegrees,
    isBackground,
    capturedAt: new Date(fix.timestamp).toISOString(),
  };
}

export function formatDistance(meters: number | null | undefined): string {
  if (typeof meters !== 'number' || !Number.isFinite(meters) || meters < 0) {
    return 'Not available';
  }
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 2 : 1)} km`;
}

export function formatStayDuration(minutes: number | null | undefined): string {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes < 0) {
    return 'Not available';
  }
  const whole = Math.round(minutes);
  if (whole < 60) return `${whole} min`;
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function formatCoordinates(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): string {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') {
    return 'Location not available';
  }
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}
